/*
 * useDataTable holds an index table's state; <DataTable> renders it. Modes:
 *
 *   client                 `data` is every row; the table searches, filters, sorts and pages it
 *   server + rowCount      `data` is one page of an endpoint that reports a total
 *   server + nextCursor    `data` is `items` of a `{ items, nextCursor }` endpoint (ADR 61):
 *                          cursor footer, no "Select all N", selection kept per loaded page
 *   ... + total            the same endpoint reporting `total` (`includeTotal` on the first page):
 *                          cursor footer and "Select all N matching"; an all-matching selection
 *                          (`exclude` descriptor) survives paging, any other is per page
 *
 * A cursor-paginated list screen:
 *
 *   route   validateSearch: indexSearchSchema(filters, { sort: ['placedAt', 'total'] })
 *           loader prefetches with listParams(deps, filters): the page's query key
 *   page    const list = useSearchListState({ search, onSearchChange, filters });
 *           const query = useOrders(storeId, list.params);
 *           const table = useDataTable({ mode: 'server', data: query.data?.items ?? [],
 *             nextCursor: query.data?.nextCursor, list, columns, filters, getRowId });
 *   filters money range: `{ type: 'number-range', currency }` (URL and API in minor units);
 *           async options: `queryFilterOptions(queryClient, { search, toOptions })`
 *   mocks   `applyListParams(rows, url.searchParams, fields)` from `src/mocks/paging.ts`
 *
 * A search, filter, sort or page-size change drops the cursor: the list starts over.
 */
import {
  useTable,
  type Column,
  type ColumnOrderState,
  type ReactTable,
  type RowData,
  type SortingState,
  type ColumnVisibilityState,
  type Updater,
} from '@tanstack/react-table';
import * as React from 'react';

import { useCursorPager, type CursorPager } from '../data-table/use-cursor-pager';

import { dataTableFeatures, type DataTableFeatures } from './features';
import { cellText, formatSort, matchFilterValue, parseSort } from './filter-codec';
import { tokenizeQuery } from './query-syntax';
import type {
  CursorListState,
  DataTableColumnDef,
  DataTableColumnMeta,
  DataTableMode,
  FilterDef,
  ListQuery,
  ListState,
  PaginationState,
  SelectionDescriptor,
  SortOption,
} from './types';
import { useDataTableSelection } from './use-data-table-selection';
import { useLocalListState } from './use-local-list-state';
import { usePersistedState } from './use-persisted-state';

export { dataTableFeatures };

interface CommonOptions<TData extends RowData> {
  /** Client mode: every row. Server mode: the current page, already filtered and sorted. */
  data: readonly TData[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- column value types vary per column
  columns: Array<DataTableColumnDef<TData, any>>;
  /** What the search bar, chips and header menus can filter on. */
  filters?: ReadonlyArray<FilterDef<TData>>;
  /** Sort menu entries. Derived from the sortable columns when omitted. */
  sortOptions?: readonly SortOption[];
  /**
   * Persists column visibility and order in this browser under this id, and
   * is the default storage key for saved views.
   */
  tableId?: string;
  initialColumnVisibility?: ColumnVisibilityState;
  initialColumnOrder?: ColumnOrderState;
  /** Controlled selection. */
  selection?: SelectionDescriptor;
  onSelectionChange?: (selection: SelectionDescriptor) => void;
}

interface PagedOptions {
  /**
   * Search, sort and filters. `useSearchListState` keeps them in the URL,
   * `useLocalListState` in React state (the default when omitted).
   */
  list?: ListState;
  /** Controlled pagination. */
  pagination?: PaginationState;
  onPaginationChange?: (pagination: PaginationState) => void;
  /** Default 20. */
  defaultPageSize?: number;
  /** `false` renders every row on one page (client mode). Default true. */
  paginate?: boolean;
}

type ClientOptions<TData extends RowData> = CommonOptions<TData> &
  PagedOptions & {
    /** `"client"` (default): the table filters, searches, sorts and pages `data` itself. */
    mode?: 'client';
    /** Stable row id. Defaults to the row index, which is fine for a fixed client-side list. */
    getRowId?: (row: TData, index: number) => string;
    rowCount?: undefined;
    nextCursor?: undefined;
    total?: undefined;
    totalCapped?: undefined;
  };

type ServerOptions<TData extends RowData> = CommonOptions<TData> &
  PagedOptions & {
    /**
     * `"server"`: `data` is one page your API already filtered, searched,
     * sorted and paged, using `list`.
     */
    mode: 'server';
    /**
     * Required: ids must be unique across pages, or "select all" and
     * cross-page selection would confuse row 3 of page 1 with row 3 of page 2.
     */
    getRowId: (row: TData, index: number) => string;
    /** Total rows matching the query, for page numbers and "Select all N". */
    rowCount: number;
    nextCursor?: undefined;
    total?: undefined;
    totalCapped?: undefined;
  };

type ServerCursorOptions<TData extends RowData> = CommonOptions<TData> & {
  /**
   * `"server"` with `nextCursor`: a cursor-paginated endpoint that returns
   * `{ items, nextCursor }` and no total (ADR 61). `data` is `items`.
   */
  mode: 'server';
  getRowId: (row: TData, index: number) => string;
  /** Cursor, limit, search, sort and filters: `useSearchListState`. */
  list: CursorListState;
  /** The loaded page's `nextCursor`: a string when there is more, null on the last page, undefined while loading. */
  nextCursor: string | null | undefined;
  /**
   * Cursor with total: rows matching the query across all pages, when the endpoint reports it
   * (ask on the first page and keep the number while paging). It turns on "Select all N" in the
   * bulk bar, and an all-matching selection then stays while the cursor moves.
   */
  total?: number | undefined;
  /** The endpoint stopped counting: `total` is a lower bound, "Select all N" is not offered. */
  totalCapped?: boolean | undefined;
  rowCount?: undefined;
  pagination?: undefined;
  onPaginationChange?: undefined;
  defaultPageSize?: undefined;
  paginate?: undefined;
};

export type UseDataTableOptions<TData extends RowData> =
  ClientOptions<TData> | ServerOptions<TData> | ServerCursorOptions<TData>;

/** What the cursor footer needs; set only in cursor mode. */
export interface DataTableCursorPaging {
  pager: CursorPager;
  nextCursor: string | null | undefined;
  hasMore: boolean;
  limit: number;
  setLimit: (limit: number) => void;
}

interface ColumnPrefs {
  visibility: ColumnVisibilityState;
  order: ColumnOrderState;
}

function isColumnPrefs(value: unknown): value is ColumnPrefs {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as ColumnPrefs).visibility === 'object' &&
    Array.isArray((value as ColumnPrefs).order)
  );
}

type AnyColumnDef<TData extends RowData> = DataTableColumnDef<TData> & {
  accessorKey?: unknown;
  accessorFn?: (row: TData, index: number) => unknown;
  columns?: Array<AnyColumnDef<TData>>;
};

interface LeafAccessor<TData> {
  id: string;
  meta: DataTableColumnMeta | undefined;
  get: (row: TData, index: number) => unknown;
}

/** Leaf columns with an accessor, id resolved the way TanStack v9 does it. */
function leafAccessors<TData extends RowData>(
  defs: ReadonlyArray<AnyColumnDef<TData>>,
): Array<LeafAccessor<TData>> {
  const out: Array<LeafAccessor<TData>> = [];
  for (const def of defs) {
    if (def.columns?.length) {
      out.push(...leafAccessors(def.columns));
      continue;
    }
    const key =
      typeof def.accessorKey === 'string' || typeof def.accessorKey === 'number'
        ? String(def.accessorKey)
        : undefined;
    const id =
      def.id ??
      key?.replaceAll('.', '_') ??
      (typeof def.header === 'string' ? def.header : undefined);
    if (!id) continue;
    const accessorFn = def.accessorFn;
    let get: LeafAccessor<TData>['get'] | undefined;
    if (accessorFn) {
      get = accessorFn;
    } else if (key !== undefined) {
      const path = key.split('.');
      get = (row) =>
        path.reduce<unknown>(
          (value, part) => (value == null ? undefined : (value as Record<string, unknown>)[part]),
          row,
        );
    }
    if (get) out.push({ id, meta: def.meta, get });
  }
  return out;
}

/** Search terms: words, and "quoted phrases" kept whole. */
function searchTerms(q: string): string[] {
  return tokenizeQuery(q)
    .map((token) => (token.quoted ? token.value : token.raw).trim().toLowerCase())
    .filter(Boolean);
}

function resolve<T>(updater: Updater<T>, current: T): T {
  return typeof updater === 'function' ? (updater as (old: T) => T)(current) : updater;
}

const DEFAULT_PAGE_SIZE = 20;
const NO_FILTERS: never[] = [];
const noop = () => undefined;

function columnLabel<TData extends RowData>(column: Column<DataTableFeatures, TData>): string {
  const { meta, header } = column.columnDef;
  return meta?.label ?? (typeof header === 'string' ? header : column.id);
}

/**
 * Builds everything `<DataTable>` renders: the TanStack table, list state,
 * filters, selection and column preferences. Rendering is `<DataTable>`'s
 * job; this hook holds no markup.
 */
export function useDataTable<TData extends RowData>(options: UseDataTableOptions<TData>) {
  const {
    data,
    columns,
    filters: filterDefs = NO_FILTERS,
    list: listProp,
    pagination: paginationProp,
    onPaginationChange,
    defaultPageSize = DEFAULT_PAGE_SIZE,
    paginate = true,
    sortOptions: sortOptionsProp,
    tableId,
    initialColumnVisibility,
    initialColumnOrder,
    selection: selectionProp,
    onSelectionChange,
  } = options;
  const mode: DataTableMode = options.mode ?? 'client';
  const server = mode === 'server';
  const cursorList =
    server && options.rowCount === undefined ? (options.list as CursorListState) : undefined;

  /* ------------------------------ list state ------------------------------ */
  const localList = useLocalListState();
  const list = listProp ?? localList;

  /* ------------------------ client-mode filtering ------------------------- */
  const accessors = React.useMemo(
    () => leafAccessors(columns as Array<AnyColumnDef<TData>>),
    [columns],
  );

  const filtered = React.useMemo(() => {
    if (server) return data;
    const tests: Array<(row: TData, index: number) => boolean> = [];

    for (const def of filterDefs) {
      const value = list.filters[def.key];
      if (!value) continue;
      const column = accessors.find((a) => a.meta?.filterKey === def.key || a.id === def.key);
      const get = def.getValue ?? column?.get;
      if (!get) continue;
      tests.push((row, index) => matchFilterValue(get(row, index), value));
    }

    const terms = searchTerms(list.q ?? '');
    if (terms.length > 0) {
      const searchable = accessors.filter((a) => a.meta?.searchable !== false);
      tests.push((row, index) => {
        const haystack = searchable
          .map((a) => cellText(a.get(row, index)).toLowerCase())
          .join('\u0000');
        return terms.every((term) => haystack.includes(term));
      });
    }

    if (tests.length === 0) return data;
    return data.filter((row, index) => tests.every((test) => test(row, index)));
  }, [accessors, data, filterDefs, list.filters, list.q, server]);

  /* ------------------------------ pagination ------------------------------ */
  const [internalPagination, setInternalPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const pagination = paginationProp ?? internalPagination;
  const setPagination = React.useCallback(
    (next: PaginationState) => {
      if (!paginationProp) setInternalPagination(next);
      onPaginationChange?.(next);
    },
    [onPaginationChange, paginationProp],
  );

  // A different query is a different list: back to its first page, and drop
  // the selection ("all matching" would otherwise silently change meaning).
  const queryKey = JSON.stringify([list.q ?? '', list.filters]);
  const [lastQueryKey, setLastQueryKey] = React.useState(queryKey);
  if (lastQueryKey !== queryKey) {
    setLastQueryKey(queryKey);
    if (!paginationProp && internalPagination.pageIndex !== 0) {
      setInternalPagination({ ...internalPagination, pageIndex: 0 });
    }
  }

  /** Undefined when the server gives no total (cursor mode without `total`). */
  const rowCount: number | undefined = cursorList
    ? options.total
    : options.mode === 'server'
      ? options.rowCount
      : filtered.length;
  // Past the last page (rows were filtered away or deleted): show the last one.
  const pageCount = Math.max(Math.ceil((rowCount ?? 0) / pagination.pageSize), 1);
  const onePage = !paginate || cursorList !== undefined;
  const effectivePagination = React.useMemo<PaginationState>(
    () =>
      onePage
        ? { pageIndex: 0, pageSize: Math.max(filtered.length, 1) }
        : {
            pageIndex: Math.min(pagination.pageIndex, pageCount - 1),
            pageSize: pagination.pageSize,
          },
    [filtered.length, onePage, pageCount, pagination],
  );

  /* ---------------------------- cursor paging ----------------------------- */
  // Another search, sort, filter set or page size is another list: its first
  // page, and a fresh "Previous" stack.
  const cursorKey = cursorList
    ? JSON.stringify([list.q ?? '', list.sort ?? '', list.filters, cursorList.limit])
    : '';
  const pager = useCursorPager({
    cursor: cursorList?.cursor,
    onNavigate: cursorList?.setCursor ?? noop,
    resetKey: cursorKey,
  });
  const lastCursorKey = React.useRef(cursorKey);
  React.useEffect(() => {
    if (lastCursorKey.current === cursorKey) return;
    lastCursorKey.current = cursorKey;
    // `useSearchListState` drops the cursor itself; this covers any other list.
    if (cursorList?.cursor !== undefined) cursorList.setCursor(undefined);
  }, [cursorKey, cursorList]);
  const nextCursor = options.mode === 'server' ? options.nextCursor : undefined;
  const cursorPaging: DataTableCursorPaging | undefined = cursorList
    ? {
        pager,
        nextCursor,
        hasMore: typeof nextCursor === 'string',
        limit: cursorList.limit,
        setLimit: cursorList.setLimit,
      }
    : undefined;

  /* --------------------------------- sort --------------------------------- */
  const sortFieldOf = React.useCallback(
    (column: Column<DataTableFeatures, TData>) => {
      const meta = column.columnDef.meta;
      if (meta?.sortField) return meta.sortField;
      if (server) {
        return column.columnDef.enableSorting === true ? column.id : undefined;
      }
      return column.getCanSort() ? column.id : undefined;
    },
    [server],
  );

  // `sort` names a field; TanStack wants the column showing it.
  const sorting = React.useMemo<SortingState>(() => {
    const parsed = parseSort(list.sort);
    if (!parsed) return [];
    const match = accessors.find((a) => (a.meta?.sortField ?? a.id) === parsed.field);
    return [{ id: match?.id ?? parsed.field, desc: parsed.direction === 'desc' }];
  }, [accessors, list.sort]);

  /* --------------------------- column preferences -------------------------- */
  const defaultPrefs = React.useMemo<ColumnPrefs>(
    () => ({
      visibility: initialColumnVisibility ?? {},
      order: initialColumnOrder ?? [],
    }),
    [initialColumnOrder, initialColumnVisibility],
  );
  const [prefs, setPrefs, resetPrefs] = usePersistedState(
    tableId ? `nucleus:index-table:${tableId}:columns` : undefined,
    defaultPrefs,
    isColumnPrefs,
  );

  /* --------------------------------- table -------------------------------- */
  const core = useTable({
    features: dataTableFeatures,
    data: filtered,
    columns,
    getRowId: options.getRowId,
    state: {
      sorting,
      pagination: effectivePagination,
      columnVisibility: prefs.visibility,
      columnOrder: prefs.order,
    },
    onSortingChange: (updater) => {
      const first = resolve(updater, sorting)[0];
      const column = first ? core.getColumn(first.id) : undefined;
      const field = column ? sortFieldOf(column) : undefined;
      list.update({
        sort:
          first && field
            ? formatSort({ field, direction: first.desc ? 'desc' : 'asc' })
            : undefined,
      });
    },
    onPaginationChange: (updater) => setPagination(resolve(updater, effectivePagination)),
    onColumnVisibilityChange: (updater) =>
      setPrefs((prev) => ({
        ...prev,
        visibility: resolve(updater, prev.visibility),
      })),
    onColumnOrderChange: (updater) =>
      setPrefs((prev) => ({ ...prev, order: resolve(updater, prev.order) })),
    enableMultiSort: false,
    manualSorting: server,
    manualPagination: server || !paginate,
    autoResetPageIndex: false,
    rowCount: cursorList ? filtered.length : (rowCount ?? filtered.length),
  });

  const rows = core.getRowModel().rows;
  const pageRowIds = rows.map((row) => row.id);

  /* ------------------------------- selection ------------------------------ */
  const selection = useDataTableSelection({
    pageRowIds,
    totalRowCount: rowCount ?? pageRowIds.length,
    value: selectionProp,
    onChange: onSelectionChange,
  });
  const { clear: clearSelection, hasSelection } = selection;
  const selectionType = selection.selection.type;
  // Cursor mode only knows the loaded page, so a selection never outlives it:
  // bulk actions then always get every selected row. With a total, "all
  // matching" is about the query, not the page, so it stays while paging.
  const selectionKey = cursorList ? cursorKey : queryKey;
  const pageKey = cursorList?.cursor ?? '';
  const lastSelectionKey = React.useRef(selectionKey);
  const lastPageKey = React.useRef(pageKey);
  React.useEffect(() => {
    const queryChanged = lastSelectionKey.current !== selectionKey;
    const pageChanged = lastPageKey.current !== pageKey;
    lastSelectionKey.current = selectionKey;
    lastPageKey.current = pageKey;
    if (!queryChanged && !pageChanged) return;
    const keep = !queryChanged && selectionType === 'exclude' && rowCount !== undefined;
    if (hasSelection && !keep) clearSelection();
  }, [clearSelection, hasSelection, pageKey, rowCount, selectionKey, selectionType]);

  /* ------------------------------ derived bits ----------------------------- */
  const sortOptions: readonly SortOption[] =
    sortOptionsProp ??
    core.getAllLeafColumns().flatMap((column) => {
      const field = sortFieldOf(column);
      return field ? [{ value: field, label: columnLabel(column) }] : [];
    });

  const getFilterDef = React.useCallback(
    (column: Column<DataTableFeatures, TData>) => {
      const key = column.columnDef.meta?.filterKey ?? column.id;
      return filterDefs.find((def) => def.key === key);
    },
    [filterDefs],
  );

  const isFiltered = Boolean(list.q) || Object.keys(list.filters).length > 0;

  const query: ListQuery = { q: list.q, sort: list.sort, filters: list.filters };

  return {
    /** The TanStack table, for anything this API doesn't cover. */
    core,
    mode,
    tableId,
    list,
    query,
    filters: filterDefs,
    /** Rows on the current page. */
    rows,
    /** Rows matching the query across all pages; undefined in cursor mode without `total`. */
    rowCount,
    /** Cursor with total: the endpoint stopped counting, `rowCount` is a lower bound. */
    rowCountCapped:
      cursorList !== undefined && options.mode === 'server' && options.totalCapped === true,
    /** Cursor mode's footer state: the pager, `nextCursor` and the page size. */
    cursorPaging,
    /** Client mode: every row matching the query (for CSV export). */
    filteredData: filtered,
    pagination: effectivePagination,
    setPagination,
    paginate,
    isFiltered,
    selection,
    sortOptions,
    sortFieldOf,
    getFilterDef,
    columnLabel,
    columnPrefs: {
      isCustomized:
        prefs.order.length > 0 || Object.values(prefs.visibility).some((visible) => !visible),
      reset: resetPrefs,
    },
  };
}

export type DataTableInstance<TData extends RowData> = ReturnType<typeof useDataTable<TData>>;

export type { ReactTable };
